/**
 * SOLID Review
 * S — Strong. One concern: the signed-in account's checkout and balance on the server, without the token
 *     ever leaving Infrastructure.
 * O — Good. A new payment call is one method here over the same gateway and token holder.
 * L — Strong. Implements the port interface exactly.
 * I — Strong. The port's two ops.
 * D — Good. The Infrastructure-internal REST gateway and token holder; the Application's port.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IEntityCheckout, IEntityBalance } from '../../domain/ports/schemas/PaymentMoves';
import type { IPortClientPaymentServer } from '../../application/ports/contracts';
import type { IGatewayAuthRest } from './authApiService';
import { restCall, type TRestResult } from '../contract/restResult';
import { getSessionToken } from '../storage/sessionTokenStorage';

const NOT_SIGNED_IN = { code: 'NOT_SIGNED_IN', message: 'Please sign in first.' } as const;

/**
 * THE PAYMENT GATEWAY (frontend strict-ports plan, FE4c, 2026-10-04) — the legacy auth adapter's two
 * payment calls, which FE3 left the Framework holding (`IAdapterAuthApi`: the checkout, and `getMe` for
 * the balance after a purchase). The token is read here, from the token holder; without one nothing is
 * sent. A server refusal keeps its words (`SERVER`); anything else could not reach the server.
 */
export class ClientPaymentServerAdapter implements IPortClientPaymentServer {
  constructor(private readonly rest: IGatewayAuthRest) {}

  async createCheckout(_ctx: IContextRequest): Promise<TRestResult<IEntityCheckout>> {
    const jwt = getSessionToken();
    if (!jwt) return { success: false, error: NOT_SIGNED_IN };
    return restCall(async (): Promise<IEntityCheckout> => ({ checkoutUrl: (await this.rest.createCheckoutSession(jwt)).checkoutUrl }));
  }

  async balance(_ctx: IContextRequest): Promise<TRestResult<IEntityBalance>> {
    const jwt = getSessionToken();
    if (!jwt) return { success: false, error: NOT_SIGNED_IN };
    return restCall(async (): Promise<IEntityBalance> => ({ matchCredits: (await this.rest.getMe(jwt)).matchCredits }));
  }
}
